import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import * as L from 'leaflet';
import { ToastrService } from 'ngx-toastr';
import { BeaconDevicesModalComponent } from '../beacon-devices-modal/beacon-devices-modal.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-beacon-device-full-details',
  templateUrl: './beacon-device-full-details.component.html',
  styleUrl: './beacon-device-full-details.component.scss'
})
export class BeaconDeviceFullDetailsComponent implements OnInit, AfterViewInit {
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLDivElement>;

  isLoading = false;
  beacon_device_uuid: string | null = null;
  alternate_reference: string | null = null;
  lading_uuid: string | null = null;  
  return_courier: string | null = null;
  tracking_number: string | null = null;
  generalInfoForm!: FormGroup;

  origin_address: string = '1380 CREDITSTONE ROAD UNIT 3, VAUGHAN, ON CANADA L4K 0J1';
  destination_address: string = '109 - 7717 BEEDIE WAY, DELTA, BC CANADA V4G 1M1';
  distanceInKm: string = '';
  distanceInMiles: string = '';
  duration: string = '';
  position_details: Array<{ [key: string]: string | number | object | boolean }> = [];

  general_info: any = [{
    "beacon_device_uuid": "",
    "beacon_id": "",
    "beacon_name": "",
    "nick_name": "",
    "tid_beacon": "",
    "beacon_type": "",
    "mac_address": "",
    "alternate_reference": "",
    "lading_uuid": "",
    "active": "",
    "return_courier": "",
    "tracking_number": "",    
  }];

  private map!: L.Map;
  private markersLayer!: L.LayerGroup;
  private routeLine!: L.Polyline;
  private mapReady = false;

  defaultColDef = {
    sortable: true,
    filter: true,
    autoHeight: true,
    cellClass: 'cell-wrap-text',
    resizable: true,
    editable: true
  };

  positionColumnDefs = [
    { field: 'beacon_id', headerName: 'Beacon ID' },
    { field: 'beacon_name', headerName: 'Beacon Name' },
    { field: 'date', headerName: 'Date' },
    { field: 'latitude', headerName: 'Latitude' },
    { field: 'longitude', headerName: 'Longitude' },
    { field: 'address', headerName: 'Address' },
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private zone: NgZone,
    private router: Router
  ) {
    this.initializeForms();
  }

  private initializeForms(): void {
    this.generalInfoForm = this.formBuilder.group({
      beacon_device_uuid: "",
      beacon_id: "",
      beacon_name: "",
      nick_name: "",
      tid_beacon: "",
      beacon_type: "",
      mac_address: "",
      alternate_reference: "",
      lading_uuid: "",
      active: "",
      return_courier: "",
      tracking_number: "",
    });
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.activatedRoute.paramMap.subscribe(params => {
      this.beacon_device_uuid = params.get('uuid');

      if (!this.beacon_device_uuid) {
        this.toastr.error('No UUID provided.');
        this.isLoading = false;
        return;
      }

      this.loadData();
    });
  }

  ngAfterViewInit(): void {
    // Leaflet map will initialize when data is ready and/or tab is opened
  }

  private async loadData(): Promise<void> {
    if (!this.beacon_device_uuid) return;

    this.isLoading = true;

    const contact_details_url = `/api/beacon_devices/beacon_devices.php?uuid=${this.beacon_device_uuid}`;

    this.http.get<any>(contact_details_url).subscribe(response => {
      console.log("Response:", response);
      this.general_info = response.data.data;
      this.position_details = response.data.positions;
      this.alternate_reference = this.general_info['alternate_reference'];
      this.lading_uuid = this.general_info['lading_uuid'];
      this.return_courier = this.general_info['return_courier'];
      this.tracking_number = this.general_info['tracking_number'];
      this.isLoading = false;
      this.checkFormReady();

      setTimeout(() => {
        if (!this.mapReady && this.mapContainer) {
          this.initializeMap();
        } else if (this.mapReady) {
          this.refreshMap();
        }
      }, 0);
    });
  }

  private checkFormReady(): void {
    console.log("General Info:", this.general_info);

    this.generalInfoForm = this.formBuilder.group({
      beacon_device_uuid: [this.general_info['beacon_device_uuid']],
      beacon_id: [this.general_info['beacon_id']],
      beacon_name: [this.general_info['beacon_name']],
      nick_name: [this.general_info['nick_name']],
      tid_beacon: [this.general_info['tid_beacon']],
      beacon_type: [this.general_info['beacon_type']],
      mac_address: [this.general_info['mac_address']],
      alternate_reference: [this.general_info['alternate_reference']],
      lading_uuid: [this.general_info['lading_uuid']],
      active: [this.general_info['active']],
      return_courier: [this.general_info['return_courier']],
      tracking_number: [this.general_info['tracking_number']],            
    });
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 1) {
      setTimeout(() => {
        if (!this.mapReady) {
          this.initializeMap();
        } else {
          this.refreshMap();
        }
      }, 150);
    }
  }

  private refreshMap(): void {
    if (!this.mapReady) return;

    this.map.invalidateSize();
    this.renderPositionsOnMap();
  }

  private getValidCoordinates(): Array<{ lat: number; lng: number; date?: string; address?: string }> {
    return (this.position_details || [])
      .map((row: any) => ({
        lat: Number(row.latitude),
        lng: Number(row.longitude),
        date: row.date,
        address: row.address
      }))
      .filter(p => !isNaN(p.lat) && !isNaN(p.lng) && p.lat !== 0 && p.lng !== 0);
  }

  private initializeMap(): void {
    if (this.mapReady || !this.mapContainer) {
      return;
    }

    this.map = L.map(this.mapContainer.nativeElement, {
      zoomControl: true
    }).setView([37.0902, -95.7129], 4);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    this.markersLayer = L.layerGroup().addTo(this.map);
    this.mapReady = true;

    setTimeout(() => {
      this.refreshMap();
    }, 200);
  }

  private renderPositionsOnMap(): void {
    if (!this.mapReady) {
      return;
    }

    const coords = this.getValidCoordinates();

    this.markersLayer.clearLayers();

    if (this.routeLine) {
      this.map.removeLayer(this.routeLine);
    }

    if (!coords.length) {
      this.map.setView([37.0902, -95.7129], 4);
      return;
    }

    const latLngs: L.LatLngExpression[] = coords.map(p => [p.lat, p.lng]);

    coords.forEach((point, index) => {
      const isLatest = index === coords.length - 1;

      const marker = L.marker([point.lat, point.lng]);

      marker.bindPopup(`
        <div>
          <strong>${isLatest ? 'Latest Position' : 'Position'}</strong><br>
          Lat: ${point.lat}<br>
          Lng: ${point.lng}<br>
          ${point.date ? `Date: ${point.date}<br>` : ''}
          ${point.address ? `Address: ${point.address}` : ''}
        </div>
      `);

      marker.addTo(this.markersLayer);
    });

    this.routeLine = L.polyline(latLngs, {
      weight: 4
    }).addTo(this.map);

    if (coords.length === 1) {
      const latest = coords[0];
      this.map.setView([latest.lat, latest.lng], 13);
      return;
    }

    this.map.fitBounds(L.latLngBounds(latLngs), {
      padding: [40, 40]
    });
  }

  linkToLading() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.width = '348px';
    dialogConfig.height = '255px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Link To Work Order',
        modalMessage: {
          beacon_device_uuid: this.beacon_device_uuid,
          alternate_reference: this.alternate_reference
        },
      },
    };

    const DialogRef = this.dialog.open(BeaconDevicesModalComponent, dialogConfig);
    DialogRef.afterClosed().subscribe(result => {
      this.loadData();
    });
  }

  setCourier() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.width = '348px';
    dialogConfig.height = '345px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Set Return Courier',
        modalMessage: {
          beacon_device_uuid: this.beacon_device_uuid,
          return_courier: this.return_courier,
          tracking_number: this.tracking_number
        },
      },
    };

    const DialogRef = this.dialog.open(BeaconDevicesModalComponent, dialogConfig);
    DialogRef.afterClosed().subscribe(result => {
      this.loadData();
    });
  }

  viewLadingDetails() {
    this.router.navigate(['work_order_details', this.lading_uuid],);
  }

}